import { 
  db, 
  auth 
} from '../firebase';
import { 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  collection, 
  where 
} from 'firebase/firestore';

/**
 * Busca un managedOwner por su email y que esté verificado
 * @param {string} email - Email del managedOwner a buscar
 * @returns {Promise<Object|null>} - Datos del managedOwner o null si no se encuentra
 */
export async function findVerifiedManagedOwnerByEmail(email) {
  try {
    console.log(`[managedOwnersService] Buscando managedOwner verificado con email: ${email}`);
    
    const managedOwnersQuery = query(
      collection(db, 'managedOwners'),
      where('email', '==', email),
      where('verified', '==', true)
    );
    
    const querySnapshot = await getDocs(managedOwnersQuery);
    
    console.log(`[managedOwnersService] Resultado de búsqueda: ${querySnapshot.empty ? 'No encontrado' : 'Encontrado'}`);
    
    if (!querySnapshot.empty) {
      const result = {
        id: querySnapshot.docs[0].id,
        ...querySnapshot.docs[0].data()
      };
      console.log('[managedOwnersService] Datos del managedOwner encontrado:', result);
      return result;
    }
    
    return null;
  } catch (error) {
    console.error('Error al buscar managedOwner verificado:', error);
    throw error;
  }
}

/**
 * Vincula un managedOwner con un usuario de Firebase Auth
 * @param {string} managedOwnerId - ID del managedOwner
 * @param {string} userId - UID del usuario en Firebase Auth
 * @returns {Promise<void>}
 */
export async function linkManagedOwnerWithUser(managedOwnerId, userId) {
  try {
    const managedOwnerRef = doc(db, 'managedOwners', managedOwnerId);
    
    await updateDoc(managedOwnerRef, {
      firebaseAuthUid: userId,
      linkedAt: new Date()
    });
    
    console.log(`ManagedOwner ${managedOwnerId} vinculado con usuario ${userId}`);
  } catch (error) {
    console.error('Error al vincular managedOwner con usuario:', error);
    throw error;
  }
}

/**
 * Verifica si el usuario actual está vinculado a algún managedOwner
 * @returns {Promise<Object|null>} - Datos del managedOwner vinculado o null
 */
export async function getCurrentUserManagedOwner() {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return null;
    }
    
    // Primero, buscar en el documento de usuario si hay un managedOwnerId
    const userDocRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data();
    
    // Si el usuario tiene skipManagedOwnerCheck = true, no hacer consultas adicionales
    if (userData.skipManagedOwnerCheck) {
      return null;
    }
    
    // Si el usuario es directamente de tipo 'managedOwner', usar su managedOwnerId
    if (userData.userType === 'managedOwner' && userData.managedOwnerId) {
      const managedOwnerId = userData.managedOwnerId;
      const managedOwnerRef = doc(db, 'managedOwners', managedOwnerId);
      
      try {
        const managedOwnerDoc = await getDoc(managedOwnerRef);
        
        if (managedOwnerDoc.exists()) {
          return {
            id: managedOwnerDoc.id,
            ...managedOwnerDoc.data()
          };
        }
      } catch (error) {
        console.error(`Error al acceder al managedOwner ${managedOwnerId}:`, error);
        console.log('Es posible que necesites actualizar las reglas de seguridad para permitir que los usuarios accedan a sus propios documentos managedOwner');
      }
    } 
    // Para usuarios 'owner' que también tienen un managedOwnerId
    else if (userData.userType === 'owner' && userData.managedOwnerId) {
      const managedOwnerId = userData.managedOwnerId;
      const managedOwnerRef = doc(db, 'managedOwners', managedOwnerId);
      
      try {
        const managedOwnerDoc = await getDoc(managedOwnerRef);
        
        if (managedOwnerDoc.exists()) {
          return {
            id: managedOwnerDoc.id,
            ...managedOwnerDoc.data()
          };
        }
      } catch (error) {
        console.error(`Error al acceder al managedOwner ${managedOwnerId}:`, error);
        console.log('Es posible que necesites actualizar las reglas de seguridad para permitir que los usuarios accedan a sus propios documentos managedOwner');
      }
    }
    // Solo buscar por firebaseAuthUid si el usuario no tiene managedOwnerId definido
    // Y no es de tipo 'applicant' o 'agent' (que no deberían ser managedOwners)
    else if (userData.userType === 'owner' && !userData.managedOwnerId) {
      try {
        const managedOwnersQuery = query(
          collection(db, 'managedOwners'),
          where('firebaseAuthUid', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(managedOwnersQuery);
        
        if (!querySnapshot.empty) {
          return {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          };
        }
      } catch (error) {
        console.error('Error al buscar managedOwner por firebaseAuthUid:', error);
        console.log('Es posible que necesites actualizar las reglas de seguridad para permitir consultas por firebaseAuthUid');
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error al obtener managedOwner del usuario:', error);
    console.log('Actualize las reglas de seguridad para permitir que los usuarios accedan a su documento managedOwner');
    return null;
  }
}

/**
 * Obtiene el agente asociado a un managedOwner
 * @param {string} managedOwnerId - ID del managedOwner
 * @returns {Promise<Object|null>} - Datos del agente o null
 */
export async function getManagedOwnerAgent(managedOwnerId) {
  try {
    const managedOwnerRef = doc(db, 'managedOwners', managedOwnerId);
    const managedOwnerDoc = await getDoc(managedOwnerRef);
    
    if (!managedOwnerDoc.exists()) {
      return null;
    }
    
    const agentId = managedOwnerDoc.data().agentId;
    
    if (!agentId) {
      return null;
    }
    
    const agentRef = doc(db, 'users', agentId);
    const agentDoc = await getDoc(agentRef);
    
    if (!agentDoc.exists()) {
      return null;
    }
    
    return {
      id: agentDoc.id,
      ...agentDoc.data()
    };
  } catch (error) {
    console.error('Error al obtener agente del managedOwner:', error);
    return null;
  }
}

/**
 * Verifica y corrige el tipo de usuario si es necesario
 * Esta función comprueba si un usuario "owner" debería ser "managedOwner"
 * @param {string} userId - ID del usuario a verificar/corregir
 * @returns {Promise<boolean>} - Devuelve true si se hizo una corrección, false en caso contrario
 */
export async function verifyAndFixUserType(userId) {
  try {
    console.log(`[managedOwnersService] Verificando tipo de usuario para: ${userId}`);
    
    // Obtener datos del usuario
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log(`[managedOwnersService] Usuario ${userId} no encontrado`);
      return false;
    }
    
    const userData = userDoc.data();
    
    // Si el usuario ya es managedOwner, no hay nada que corregir
    if (userData.userType === 'managedOwner') {
      console.log(`[managedOwnersService] Usuario ${userId} ya es managedOwner`);
      return false;
    }
    
    // Si el usuario es owner, verificar si debería ser managedOwner
    if (userData.userType === 'owner' && userData.email) {
      console.log(`[managedOwnersService] Verificando si ${userData.email} corresponde a un managedOwner`);
      
      try {
        const managedOwnerData = await findVerifiedManagedOwnerByEmail(userData.email);
        
        if (managedOwnerData) {
          console.log(`[managedOwnersService] Se encontró un managedOwner para ${userData.email}, actualizando tipo de usuario`);
          
          // Actualizar el tipo de usuario a managedOwner
          await updateDoc(userRef, {
            userType: 'managedOwner',
            managedOwnerId: managedOwnerData.id,
            agentId: managedOwnerData.agentId || null
          });
          
          // Vincular el managedOwner con el usuario
          await linkManagedOwnerWithUser(managedOwnerData.id, userId);
          
          console.log(`[managedOwnersService] Usuario ${userId} actualizado correctamente a managedOwner`);
          return true;
        }
      } catch (error) {
        console.error(`[managedOwnersService] Error al verificar/actualizar tipo de usuario:`, error);
      }
    }
    
    return false;
  } catch (error) {
    console.error(`[managedOwnersService] Error en verifyAndFixUserType:`, error);
    return false;
  }
} 